import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { getAppBasename, useAppRouter } from '../hooks/useAppRouter';
import { redirectToSignIn } from '../services/provider/authService';
import { getPublicRuntimeConfig } from '../libs/init/getPublicRuntimeConfig';
import { shouldRouteClientSide } from '../utils/shouldRouteClientSide';

enum State {
  Initial = 0,
  RoutingInProgress = 1,
  NotFound = 2,
}

const PENDING_HASH_KEY = 'staticSiteRouter_pendingHash';

/*
 * When deployed via static site generation (SSG) the edge router directs the intial
 * page request to this router page. From here the Next.js page router takes over to find
 * the required page.
 * This approach allows us to make full use of the Next.js page router including
 * dyanmic path segmenents, catch all routes, etc.
 */
export function StaticSiteRouter(): ReactElement {
  const router = useAppRouter();

  const [state, setState] = useState(State.Initial);

  const requiresClientSideRouting = useMemo(() => {
    const runsInBrowser = typeof window !== 'undefined';
    const clientSide = shouldRouteClientSide(router.asPath, router.pathname);
    return runsInBrowser && clientSide;
  }, [router.asPath, router.pathname]);

  useEffect(() => {
    if (!requiresClientSideRouting || state !== State.Initial) {
      return;
    }

    setState(State.RoutingInProgress);

    (async () => {
      try {
        // Check if URL has a hash fragment. Next.js has a bug where it treats
        // router.push as a hash-only change when the browser URL path matches
        // the target path (even though the loaded component is different).
        // This happens because Next.js uses the browser URL to determine the
        // "current route" during hydration, not the actually-loaded page.
        const hashIndex = router.asPath.indexOf('#');
        const hasHash = hashIndex >= 0;

        if (hasHash) {
          // Store the hash and navigate without it first, then restore after load.
          const pathWithoutHash = router.asPath.slice(0, hashIndex);
          const hash = router.asPath.slice(hashIndex); // includes the # symbol

          sessionStorage.setItem(PENDING_HASH_KEY, hash);
          window.location.href = `${getAppBasename()}${pathWithoutHash}`;
          return;
        }

        // Check if we have a pending hash to restore (from a previous navigation)
        const pendingHash = sessionStorage.getItem(PENDING_HASH_KEY);

        await router.push(router.asPath);

        // Restore the pending hash AFTER navigation completes
        if (pendingHash) {
          sessionStorage.removeItem(PENDING_HASH_KEY);
          // Use history.replaceState to add hash without triggering another navigation
          history.replaceState(null, '', window.location.href + pendingHash);
        }
      } catch {
        setState(State.NotFound);
      }
    })();
  }, [requiresClientSideRouting, state, setState, router]);

  // The router didn't recognise the route
  useEffect(() => {
    if (state === State.NotFound) {
      const { paths } = getPublicRuntimeConfig();
      redirectToSignIn(`/v2${paths.dashboard}`);
    }
  }, [state]);

  // Routing is still in progress
  return <></>;
}

export default StaticSiteRouter;
